import { Badge, BrandImage, Button, fmt } from "@nabble/ui";
import { useRef } from "react";
import type { BrandImageInfo } from "../../../api/adminBranding";
import { BRAND_FILE_TYPES, brandImageUrl, type BrandSlot } from "../../../model/brandSlots";

export interface BrandSlotCardProps {
  slot: BrandSlot;
  image?: BrandImageInfo;
  busy: boolean;
  onUpload: (file: File) => void;
  onRemove: () => void;
}

/** One brand image: its preview (or "Not uploaded"), what it is for, and upload / remove. */
export function BrandSlotCard({ slot, image, busy, onUpload, onRemove }: BrandSlotCardProps): React.JSX.Element {
  const input = useRef<HTMLInputElement>(null);
  return (
    <article className="nb-card brand-slot" aria-label={slot.label}>
      <div className={`brand-slot-preview${slot.dark ? " is-dark" : ""}`}>
        {image ? (
          <BrandImage src={brandImageUrl(slot.key, image.updatedAt)} alt={slot.label} />
        ) : (
          <span className="brand-slot-empty">Not uploaded</span>
        )}
      </div>
      <div className="brand-slot-body">
        <div className="brand-slot-title">
          <strong>{slot.label}</strong>
          {image ? <Badge label="Uploaded" tone="success" /> : <Badge label="Missing" tone="neutral" />}
        </div>
        <p className="brand-slot-desc">{slot.usedFor}</p>
        <p className="brand-slot-meta">
          {image
            ? `${Math.round(image.size / 1024)} KB · updated ${fmt.date(image.updatedAt)}`
            : `Recommended ${slot.size} px, PNG, JPEG or WebP`}
        </p>
        <div className="brand-slot-actions">
          <input
            ref={input}
            type="file"
            hidden
            accept={BRAND_FILE_TYPES.join(",")}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) onUpload(file);
            }}
          />
          <Button label={image ? "Replace" : "Upload"} size="sm" variant="secondary" icon="image" loading={busy}
            onClick={() => input.current?.click()} />
          {image ? <Button label="Remove" size="sm" variant="ghost" disabled={busy} onClick={onRemove} /> : null}
        </div>
      </div>
    </article>
  );
}
